// ============================================
// Bucket / "Again" Word List Modal
// ============================================
//
// Opens when a stat card (New / Learning / Review / Mastered) or the
// "Again · needs work" chip is tapped in the study-mode stats panel. Lists
// the words behind that count so the user can focus on / scan a specific
// bucket — answering Violette's "show what's in the Review and Again
// sections" feedback.
//
// Words come from the signal selectors (`bucketWords` / `againWords`), so the
// list is struggle-sorted (worst-accuracy first) and recomputes reactively
// if progress changes while the modal is open. Rows are static (no per-row
// motion) so the 500–600-word New bucket renders without jank.

import { Modal, SRSBadge, Button } from '@/components/ui';
import { useAudio } from '@/hooks';
import {
  bucketWords,
  againWords,
  enterDrill,
  wordProgress,
  type BucketView
} from '@/state/signals';
import type { Word } from '@/types';

const VIEW_LABEL: Record<BucketView, string> = {
  new: 'New',
  learning: 'Learning',
  review: 'Review',
  mastered: 'Mastered',
  again: 'Again · needs work'
};

// Buckets that offer a focused practice drill — the two sections Violette named.
const DRILLABLE: BucketView[] = ['review', 'again'];

interface BucketListModalProps {
  /** Which list to show; `null` keeps the modal closed. */
  view: BucketView | null;
  onClose: () => void;
}

export function BucketListModal({ view, onClose }: BucketListModalProps) {
  const { playAudio } = useAudio();

  // Resolve the words for the active view. Reads deck + wordProgress inside
  // the selectors, so this recomputes if either changes while open.
  const words = view === null ? [] : view === 'again' ? againWords() : bucketWords(view);
  const label = view === null ? '' : VIEW_LABEL[view];
  const canDrill = view !== null && DRILLABLE.includes(view) && words.length > 0;

  return (
    <Modal
      isOpen={view !== null}
      onClose={onClose}
      title={`${label} (${words.length})`}
      size="lg"
    >
      {canDrill && view !== null && (
        <Button
          variant="primary"
          onClick={() => {
            enterDrill(view);
            onClose();
          }}
          className="w-full mb-3"
          ariaLabel={`Practice these ${words.length} ${label} words`}
        >
          🎯 Drill these {words.length} words
        </Button>
      )}
      {words.length === 0 ? (
        <p className="text-center text-gray-500 py-8">
          {view === 'again'
            ? 'Nothing to redo right now — nice work! 🎉'
            : 'No words here yet — keep studying!'}
        </p>
      ) : (
        <ul className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100 -mx-2 px-2">
          {words.map(word => (
            <WordRow
              key={word.japanese}
              word={word}
              onPlay={() => playAudio(word.japanese)}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
}

interface WordRowProps {
  word: Word;
  onPlay: () => void;
}

function WordRow({ word, onPlay }: WordRowProps) {
  const state = wordProgress.value[word.japanese]?.state ?? 'new';

  return (
    <li className="flex items-center gap-3 py-2.5">
      <SRSBadge state={state} className="w-7 h-7 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-gray-900 truncate">{word.japanese}</span>
          <span className="text-sm text-gray-500 truncate">{word.reading}</span>
        </div>
        <div className="text-sm text-gray-600 truncate">{word.meaning}</div>
      </div>
      <button
        type="button"
        onClick={onPlay}
        aria-label={`Play audio for ${word.japanese}`}
        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      >
        🔊
      </button>
    </li>
  );
}
